import { useMemo } from "react";
import { Badge, Flex, Text, useMantineTheme } from "@mantine/core";
import { MantineReactTable, type MRT_ColumnDef } from "mantine-react-table";
import { formatDate, formatINR } from "@/shared/utils/format.utils";
import { OrderTypeBadge } from "@/pages/admin/AdminStatusBadge";

type InvestmentRow = {
  id: string;
  rowKind: "lumpsum" | "sxp";
  amcName: string | null;
  schemeName: string;
  goalName: string | null;
  type: string | null;
  orderStatus: string | null;
  sxpStatus: string | null;
  invested: number | null;
  allotmentUnits: number | null;
  allotmentNav: number | null;
  allotmentDate: string | null;
  folioNumber: string | null;
  currentValue: number | null;
  latest_nav: number | null;
  latest_nav_date: string | null;
  rtaRemarks: string | null;
  frequency: string | null;
  startDate: string | null;
  endDate: string | null;
  installmentCount: number | null;
  createdAt: string | null;
};

type Props = {
  orders: any[]; // lumpsums from getById (aliased as `orders`)
  sxps: any[];
};

const dash = <span style={{ color: "var(--mantine-color-slate-4)" }}>—</span>;

const SXP_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  reg: { label: "Registered", color: "blue" },
  sxp_2fa_pending: { label: "2FA Pending", color: "orange" },
  active: { label: "Active", color: "green" },
  sxp_order_triggered: { label: "Order Triggered", color: "teal" },
  paused: { label: "Paused", color: "yellow" },
  sxp_investor_canc: { label: "Investor Cancelled", color: "red" },
  cancelled: { label: "Cancelled", color: "red" },
  mandate_unlink: { label: "Mandate Unlinked", color: "red" },
  autocancelled: { label: "Auto-cancelled", color: "red" },
  matured: { label: "Matured", color: "gray" },
};

const FREQUENCY_CONFIG: Record<string, { label: string; color: string }> = {
  m: { label: "Monthly", color: "indigo" },
  w: { label: "Weekly", color: "violet" },
  d: { label: "Daily", color: "cyan" },
  f: { label: "Fortnightly", color: "teal" },
  q: { label: "Quarterly", color: "grape" },
  h: { label: "Half-yearly", color: "blue" },
  y: { label: "Yearly", color: "lime" },
};

const LUMPSUM_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  done: { label: "Done", color: "green" },
  pending: { label: "Pending", color: "yellow" },
  failed: { label: "Failed", color: "red" },
  cancelled: { label: "Cancelled", color: "red" },
  processing: { label: "Processing", color: "blue" },
};

function SxpStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return dash;
  const config = SXP_STATUS_CONFIG[status] ?? { label: status, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

function FrequencyBadge({ frequency }: { frequency: string | null | undefined }) {
  if (!frequency) return dash;
  const config = FREQUENCY_CONFIG[frequency] ?? { label: frequency, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

function LumpsumStatusBadge({ status }: { status: string | null | undefined }) {
  if (!status) return dash;
  const config = LUMPSUM_STATUS_CONFIG[status] ?? { label: status, color: "gray" };
  return (
    <Badge color={config.color} variant="light" size="sm">
      {config.label}
    </Badge>
  );
}

export default function InvestmentsTab({ orders, sxps }: Props) {
  const theme = useMantineTheme();

  const rows: InvestmentRow[] = useMemo(() => {
    // Exclude redemptions (type R) — keep purchases (P) and switches (S)
    const lumpsumRows: InvestmentRow[] = orders
      .filter((l) => l.type !== "R")
      .map((l) => ({
        id: `lumpsum:${l.id}`,
        rowKind: "lumpsum",
        amcName: l.masterScheme?.scheme_amc_name ?? null,
        schemeName: l.masterScheme?.name ?? l.scheme ?? "—",
        goalName: l.goal?.goalName ?? null,
        type: l.type ?? null,
        orderStatus: l.status ?? null,
        sxpStatus: null,
        invested: l.amount ?? null,
        allotmentUnits: l.allotment_units ?? null,
        allotmentNav: l.allotment_nav ?? null,
        allotmentDate: l.allotment_date ?? null,
        folioNumber: l.folio ?? null,
        currentValue: l.currentValue ?? null,
        latest_nav: l.masterScheme?.latest_nav ?? null,
        latest_nav_date: l.masterScheme?.latest_nav_date ?? null,
        rtaRemarks: l.rtaRemarks ?? null,
        frequency: null,
        startDate: null,
        endDate: null,
        installmentCount: null,
        createdAt: l.createdAt ?? null,
      }));

    const sxpRows: InvestmentRow[] = sxps.map((s) => ({
      id: `sxp:${s.id}`,
      rowKind: "sxp",
      amcName: s.masterScheme?.scheme_amc_name ?? null,
      schemeName: s.masterScheme?.name ?? s.srcScheme ?? "—",
      goalName: s.goal?.goalName ?? null,
      type: s.sxpType ?? null,
      orderStatus: null,
      sxpStatus: s.status ?? null,
      invested: s.actualInvested ?? null,
      allotmentUnits: s.allotmentUnits ?? null,
      allotmentNav: null,
      allotmentDate: null,
      folioNumber: null,
      currentValue: s.currentValue ?? null,
      latest_nav: s.masterScheme?.latest_nav ?? null,
      latest_nav_date: s.masterScheme?.latest_nav_date ?? null,
      rtaRemarks: null,
      frequency: s.frequency ?? null,
      startDate: s.startDate ?? null,
      endDate: s.endDate ?? null,
      installmentCount: s.installmentCount ?? null,
      createdAt: s.createdAt ?? null,
    }));

    return [...lumpsumRows, ...sxpRows];
  }, [orders, sxps]);

  const columns: MRT_ColumnDef<InvestmentRow>[] = useMemo(
    () => [
      {
        accessorKey: "schemeName",
        header: "Scheme Name",
        size: 260,
        Cell: ({ cell }) => cell.getValue<string>() || dash,
      },
      {
        accessorKey: "goalName",
        header: "Goal Name",
        size: 160,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "type",
        header: "Type",
        size: 110,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <OrderTypeBadge type={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "orderStatus",
        header: "Order Status",
        size: 130,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <LumpsumStatusBadge status={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "invested",
        header: "Invested",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => formatINR(cell.getValue<number | null>()),
      },
      {
        accessorKey: "allotmentUnits",
        header: "Allotment Units",
        size: 150,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? v.toFixed(4) : dash;
        },
      },
      {
        accessorKey: "allotmentNav",
        header: "Allotment NAV",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? v.toFixed(4) : dash;
        },
      },
      {
        accessorKey: "allotmentDate",
        header: "Allotment Date",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "folioNumber",
        header: "Folio No.",
        size: 140,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "currentValue",
        header: "Current Value",
        size: 150,
        enableColumnFilter: false,
        enableSorting: true,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? (
            <span style={{ color: "var(--mantine-color-green-7)" }}>{formatINR(v)}</span>
          ) : (
            dash
          );
        },
      },
      {
        accessorKey: "latest_nav",
        header: "Latest NAV",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? v.toFixed(4) : dash;
        },
      },
      {
        accessorKey: "latest_nav_date",
        header: "NAV Date",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "sxpStatus",
        header: "SXP Status",
        size: 160,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <SxpStatusBadge status={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "frequency",
        header: "Frequency",
        size: 130,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) => <FrequencyBadge frequency={cell.getValue<string | null>()} />,
      },
      {
        accessorKey: "installmentCount",
        header: "Installments Done",
        size: 160,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<number | null>();
          return v != null ? String(v) : dash;
        },
      },
      {
        accessorKey: "startDate",
        header: "Start Date",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "endDate",
        header: "End Date",
        size: 130,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
      {
        accessorKey: "rtaRemarks",
        header: "RTA Remarks",
        size: 180,
        enableColumnFilter: false,
        Cell: ({ cell }) => cell.getValue<string | null>() ?? dash,
      },
      {
        accessorKey: "createdAt",
        header: "Created At",
        size: 140,
        enableColumnFilter: false,
        Cell: ({ cell }) => {
          const v = cell.getValue<string | null>();
          return v ? formatDate(v) : dash;
        },
      },
    ],
    []
  );

  return (
    <Flex direction="column" gap="md">
      <Text fw={500} fz="md">
        All investments
      </Text>

      <MantineReactTable
        columns={columns}
        data={rows}
        enableRowNumbers
        enableStickyHeader
        enableColumnPinning
        enableColumnResizing
        columnResizeMode="onEnd"
        layoutMode="semantic"
        enableGlobalFilter={false}
        positionToolbarAlertBanner="none"
        initialState={{
          density: "xs",
          columnPinning: { left: ["mrt-row-numbers", "schemeName"] },
        }}
        mantineTableBodyCellProps={{
          style: {
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            borderRight: "1px solid var(--mantine-color-gray-2)",
          },
        }}
        mantineTableHeadCellProps={{
          style: {
            fontWeight: 400,
            fontSize: "var(--mantine-font-size-sm)",
            color: theme.colors.slate[5],
            borderRight: "1px solid var(--mantine-color-gray-2)",
            backgroundColor: "var(--mantine-color-gray-0)",
          },
        }}
        mantineTableContainerProps={{
          style: {
            maxHeight: "calc(100vh - 300px)",
            minHeight: "100px",
            width: "100%",
            overflowX: "auto",
          },
        }}
      />
    </Flex>
  );
}
